<template>
  <div>
    <Navigation />
    <div class="wrap">
      <div class="head">
        <div>
          <h1>Catalogue</h1>
          <p class="lede">Categories and brands used when adding products.</p>
        </div>
      </div>

      <div class="two-col">
        <!-- CATEGORIES -->
        <div class="card">
          <div class="col-head">
            <h3>Categories</h3>
            <button class="primary" @click="openCategory(null)">+ Add category</button>
          </div>
          <div v-if="categories.length" class="cat-tree">
            <div v-for="c in tree" :key="c.id" class="cat-node">
              <div class="cat-row">
                <span class="cat-name">{{ c.name }}</span>
                <span class="cat-actions">
                  <button class="secondary small" @click="openCategory(c)">Edit</button>
                  <button class="danger small" @click="removeCategory(c)">Delete</button>
                </span>
              </div>
              <div v-if="c.children.length" class="cat-children">
                <div v-for="ch in c.children" :key="ch.id" class="cat-node child">
                  <div class="cat-row">
                    <span class="cat-name">↳ {{ ch.name }}</span>
                    <span class="cat-actions">
                      <button class="secondary small" @click="openCategory(ch)">Edit</button>
                      <button class="danger small" @click="removeCategory(ch)">Delete</button>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <p v-else class="muted center">No categories yet.</p>
        </div>

        <!-- BRANDS -->
        <div class="card">
          <div class="col-head">
            <h3>Brands</h3>
            <button class="primary" @click="openBrand(null)">+ Add brand</button>
          </div>
          <div v-if="brands.length" class="brand-list">
            <div v-for="b in brands" :key="b.id" class="brand-row">
              <span class="brand-name">{{ b.name }}</span>
              <button class="danger small" @click="removeBrand(b)">Delete</button>
            </div>
          </div>
          <p v-else class="muted center">No brands yet.</p>
        </div>
      </div>
    </div>

    <!-- CATEGORY modal -->
    <div v-if="showCat" class="modal-back" @click.self="showCat=false">
      <div class="modal card">
        <h3>{{ catForm.id ? 'Edit Category' : 'New Category' }}</h3>
        <label>Name</label>
        <input v-model="catForm.name" />
        <label>Parent</label>
        <select v-model.number="catForm.parentId">
          <option :value="0">— top level —</option>
          <option v-for="c in topLevel" :key="c.id" :value="c.id" :disabled="c.id === catForm.id">{{ c.name }}</option>
        </select>
        <div class="modal-actions">
          <button class="secondary" @click="showCat=false">Cancel</button>
          <button class="primary" @click="saveCategory" :disabled="saving">{{ saving ? 'Saving…' : 'Save' }}</button>
        </div>
        <p class="err" v-if="err">{{ err }}</p>
      </div>
    </div>

    <!-- BRAND modal -->
    <div v-if="showBr" class="modal-back" @click.self="showBr=false">
      <div class="modal card">
        <h3>New Brand</h3>
        <label>Name</label>
        <input v-model="brForm.name" />
        <div class="modal-actions">
          <button class="secondary" @click="showBr=false">Cancel</button>
          <button class="primary" @click="saveBrand" :disabled="saving">{{ saving ? 'Saving…' : 'Save' }}</button>
        </div>
        <p class="err" v-if="err">{{ err }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import Navigation from '@/components/paysol-nav/Navigation.vue';
import { paysol, execute } from '@/api';

export default {
  name: 'CatalogAdmin',
  components: { Navigation },
  data() {
    return {
      categories: [], brands: [],
      showCat: false, showBr: false, saving: false, err: '',
      catForm: { id: 0, name: '', parentId: 0 },
      brForm: { name: '' },
    };
  },
  computed: {
    topLevel() { return this.categories.filter(c => !c.parent_id || c.parent_id === 0); },
    tree() {
      const byParent = {};
      this.categories.forEach(c => {
        const p = c.parent_id || 0;
        if (!byParent[p]) byParent[p] = [];
        byParent[p].push({ ...c, children: [] });
      });
      const walk = (parentId) => (byParent[parentId] || []).map(node => ({
        ...node, children: walk(node.id)
      }));
      return walk(0);
    }
  },
  async mounted() { await this.load(); },
  methods: {
    async load() {
      const [cats, brs] = await Promise.all([
        execute({ function: 'listCategories' }, paysol),
        execute({ function: 'listBrands' }, paysol),
      ]);
      if (cats.data.success) this.categories = cats.data.data;
      if (brs.data.success) this.brands = brs.data.data;
    },
    openCategory(c) {
      this.catForm = c
        ? { id: c.id, name: c.name, parentId: c.parent_id || 0 }
        : { id: 0, name: '', parentId: 0 };
      this.err = ''; this.showCat = true;
    },
    async saveCategory() {
      this.saving = true; this.err = '';
      try {
        const { data } = await execute({ function: 'saveCategory', ...this.catForm }, paysol);
        if (!data.success) { this.err = data.message; return; }
        this.showCat = false;
        await this.load();
      } catch (e) { this.err = e.response?.data?.message || 'Save failed.'; }
      finally { this.saving = false; }
    },
    async removeCategory(c) {
      if (!confirm(`Delete category "${c.name}"?`)) return;
      const { data } = await execute({ function: 'deleteCategory', id: c.id }, paysol);
      if (data.success) await this.load();
      else alert(data.message);
    },
    openBrand() { this.brForm = { name: '' }; this.err = ''; this.showBr = true; },
    async saveBrand() {
      this.saving = true; this.err = '';
      try {
        const { data } = await execute({ function: 'saveBrand', ...this.brForm }, paysol);
        if (!data.success) { this.err = data.message; return; }
        this.showBr = false;
        await this.load();
      } catch (e) { this.err = e.response?.data?.message || 'Save failed.'; }
      finally { this.saving = false; }
    },
    async removeBrand(b) {
      if (!confirm(`Delete brand "${b.name}"?`)) return;
      const { data } = await execute({ function: 'deleteBrand', id: b.id }, paysol);
      if (data.success) await this.load();
      else alert(data.message);
    },
  }
};
</script>

<style scoped>
.wrap { max-width: 1100px; margin: 0 auto; padding: 24px; }
.head { display: flex; justify-content: space-between; align-items: end; margin-bottom: 20px; }
h1 { font-size: 26px; margin: 0 0 4px; color: var(--paysol-text); }
.lede { color: var(--paysol-muted); margin: 0; }
.two-col { display: grid; grid-template-columns: 1.4fr 1fr; gap: 16px; }
@media (max-width: 800px) { .two-col { grid-template-columns: 1fr; } }
.col-head { display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px; }
.col-head h3 { color: var(--paysol-muted); font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px; margin: 0; }
.cat-tree { display: flex; flex-direction: column; gap: 4px; }
.cat-node { }
.cat-row { display: flex; align-items: center; justify-content: space-between; padding: 8px 12px; border-radius: 6px; background: var(--paysol-bg-elev); border: 1px solid var(--paysol-line); }
.cat-node.child .cat-row { background: transparent; padding-left: 22px; }
.cat-name { color: var(--paysol-text); font-weight: 500; font-size: 13px; }
.cat-actions { display: flex; gap: 4px; }
.cat-children { display: flex; flex-direction: column; gap: 2px; margin: 4px 0 8px 8px; padding-left: 8px; border-left: 1px dashed var(--paysol-line); }
.brand-list { display: flex; flex-direction: column; gap: 4px; }
.brand-row { display: flex; align-items: center; justify-content: space-between; padding: 8px 12px; background: var(--paysol-bg-elev); border: 1px solid var(--paysol-line); border-radius: 6px; }
.brand-name { color: var(--paysol-text); font-size: 13px; font-weight: 500; }
button.small { padding: 4px 8px; font-size: 11px; }
.muted { color: var(--paysol-muted); }
.center { text-align: center; padding: 20px 0; }
.modal-back { position: fixed; inset: 0; background: rgba(0,0,0,0.6); display: flex; align-items: center; justify-content: center; z-index: 50; padding: 24px; }
.modal { width: 100%; max-width: 420px; }
.modal-actions { display: flex; justify-content: flex-end; gap: 8px; margin-top: 20px; padding-top: 14px; border-top: 1px solid var(--paysol-line); }
.err { color: var(--paysol-danger); font-size: 12px; margin-top: 8px; }
</style>
